import { inject } from '@angular/core';
import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { Observable, firstValueFrom, throwError, timer } from 'rxjs';
import { exhaustMap, takeWhile, timeout } from 'rxjs/operators';
import type { ImportOverrides } from '@lite-workspace/import-pipeline-core';
import {
  PersonImportApiService,
  isDefiniteOff,
  type ImportPreviewRequest,
  type ImportPreviewResult,
  type ImportRun,
} from '../infrastructure/person-import-api.service';
import { ImportArchiveUploadService, type ImportArchiveUploadProgress } from '../infrastructure/import-archive-upload.service';
import { environment } from '@environment';

/** Тик опроса прогона: PENDING_FACE закрывает распознавание, а оно живёт секундами, не миллисекундами. */
export const IMPORT_RUN_POLL_MS = 3000;
/** Потолок одного запроса: молчащее соединение — не «ещё думает», а отказ тика (exhaustMap отпустит следующий). */
export const IMPORT_REQUEST_CEILING_MS = 15_000;
/** Потолок числа тиков одного опроса — 200×3с ≈ 10 минут: дальше это уже не «дожимаем», а забытый опрос. */
export const IMPORT_RUN_POLL_MAX_TICKS = 200;

export const IMPORT_RUN_TERMINAL: ReadonlySet<ImportRun['status']> = new Set(['APPLIED', 'FAILED']);

/**
 * Опрос прогона до условия. exhaustMap: пока ответ тика в пути, новые тики пропускаются (switchMap
 * рвал бы уже идущий запрос — async-state-mutation.md п.4); потолок на запрос превращает молчание в
 * ошибку тика, потолок на число тиков превращает забытый/зависший опрос в ошибку самого Observable
 * вместо бесконечного polling. Досрочно — отпиской подписчика (закрытие диалога).
 */
function pollUntil(
  api: PersonImportApiService,
  projectId: string,
  runId: string,
  done: (run: ImportRun) => boolean,
): Observable<ImportRun> {
  return timer(IMPORT_RUN_POLL_MS, IMPORT_RUN_POLL_MS).pipe(
    exhaustMap(tick =>
      tick >= IMPORT_RUN_POLL_MAX_TICKS
        ? throwError(() => new Error(`Опрос прогона превысил потолок в ${IMPORT_RUN_POLL_MAX_TICKS} тиков`))
        : api.getRun(projectId, runId).pipe(timeout(IMPORT_REQUEST_CEILING_MS)),
    ),
    takeWhile(run => !done(run), true),
  );
}

export type PersonImportFailure = 'unavailable' | 'forbidden' | 'error';

export function importFailureOf(error: unknown): PersonImportFailure {
  if (!isDefiniteOff(error)) return 'error';
  const status = (error as { status?: number }).status;
  if (status === 404) return 'unavailable';
  if (status === 403) return 'forbidden';
  return 'error';
}

interface PersonImportState {
  /** null — флаг ещё не читали; false закрывает кнопку «Имена из названий файлов». */
  enabled: boolean | null;
  previewing: boolean;
  applying: boolean;
  failure: PersonImportFailure | null;
}

const initialState: PersonImportState = {
  enabled: null,
  previewing: false,
  applying: false,
  failure: null,
};

/**
 * Импорт персон: флаг, preview/apply и опрос прогона. Стор инжектится напрямую
 * (ADR 0004): у страницы «Лица» — ради флага, у мастера — ради вызовов; результаты возвращаются
 * вызывающему, состояние прогона живёт в мастере (у него же генерационный стамп).
 */
export const PersonImportStore = signalStore(
  { providedIn: 'root' },
  environment.storeWithDevTools('personImport'),
  withState(initialState),
  withMethods((store, api = inject(PersonImportApiService), archiveUpload = inject(ImportArchiveUploadService)) => {
    /**
     * Генерационные стампы стора (async-state-mutation.md, вариант B) — ОДИН на операцию, не общий.
     * Стор — root-singleton, а вызывают его переоткрываемые диалоги: закрытие мастера не отменяет HTTP,
     * и запоздавший ответ «ничьего» запроса иначе ложится поверх свежего состояния. Общий счётчик на
     * все три операции гасил бы чужую запись чужим инкрементом: preview, стартовавший после loadEnabled,
     * но раньше её ответа, отбросил бы enabled по факту одного лишь своего запуска, не своего отказа.
     * Каждый метод снимает СВОЙ номер ДО первого await и пишет в стейт, только если он всё ещё последний.
     */
    let loadEnabledGeneration = 0;
    let previewGeneration = 0;
    let applyGeneration = 0;

    return {
      async loadEnabled(): Promise<boolean> {
        const gen = ++loadEnabledGeneration;
        const enabled = await firstValueFrom(api.isEnabled());
        if (gen === loadEnabledGeneration) updateState(store, '[PersonImport] Flag Loaded', { enabled });
        return enabled;
      },

      /** Сборка предпросмотра. Отказ 404 гасит флаг: гейтвей сказал «фичи нет», кнопке дальше нечего открывать. */
      async preview(projectId: string, request: ImportPreviewRequest = {}): Promise<ImportPreviewResult | null> {
        const gen = ++previewGeneration;
        updateState(store, '[PersonImport] Preview Started', { previewing: true, failure: null });
        try {
          const result = await firstValueFrom(api.preview(projectId, request).pipe(timeout(IMPORT_REQUEST_CEILING_MS)));
          if (gen === previewGeneration) updateState(store, '[PersonImport] Preview Loaded', { previewing: false });
          return result;
        } catch (error: unknown) {
          const failure = importFailureOf(error);
          if (gen === previewGeneration) {
            updateState(store, '[PersonImport] Preview Failed', {
              previewing: false,
              failure,
              ...(failure === 'unavailable' ? { enabled: false } : {}),
            });
          }

          return null;
        }
      },

      async apply(projectId: string, runId: string, overrides: ImportOverrides): Promise<ImportRun | null> {
        const gen = ++applyGeneration;
        updateState(store, '[PersonImport] Apply Started', { applying: true, failure: null });
        try {
          const run = await firstValueFrom(api.apply(projectId, runId, overrides).pipe(timeout(IMPORT_REQUEST_CEILING_MS)));
          if (gen === applyGeneration) updateState(store, '[PersonImport] Apply Done', { applying: false });
          return run;
        } catch (error: unknown) {
          const failure = importFailureOf(error);
          if (gen === applyGeneration) updateState(store, '[PersonImport] Apply Failed', { applying: false, failure });
          return null;
        }
      },

      /** Прогресс летит наружу как есть — общий стейт стора не трогает, поэтому стамп не нужен. */
      uploadArchive(projectId: string, file: File): Observable<ImportArchiveUploadProgress> {
        return archiveUpload.upload(projectId, file);
      },

      /**
       * Опрос прогона до терминального статуса — APPLIED/FAILED (включительно). В общий стейт не
       * пишет, поэтому стамп не нужен.
       */
      pollRun(projectId: string, runId: string): Observable<ImportRun> {
        return pollUntil(api, projectId, runId, run => IMPORT_RUN_TERMINAL.has(run.status));
      },

      /**
       * Опрос архивного разбора: прогон живёт в EXTRACTING, пока сервер тянет zip из хранилища и
       * раскладывает кадры. Завершается на ЛЮБОМ другом статусе — PREVIEW (набор готов) или FAILED
       * (пакет отвергнут), и решение «что показать» принимает вызывающий.
       */
      pollExtraction(projectId: string, runId: string): Observable<ImportRun> {
        return pollUntil(api, projectId, runId, run => run.status !== 'EXTRACTING');
      },
    };
  }),
);
